// sitelen nasin — a hand-drawn block script for toki pona.
//
// A syllabic shorthand: 9 consonant gestures x 5 vowel endings, one pen
// stroke per syllable, syllable strokes stacked into blocks of up to 3 bands.
//
// Vowel endings:  a = plain   i = flick   e = wave tail
//                 o = closed circle      u = open hook
// Extras:         final -n = backward flick (fused into the stroke)
//                 word-initial a- = entry loop on the next stroke (fused)
//                 other initial vowels / vowel-only words = carrier dash
//
// This module is pure data + geometry: parsing toki pona words into
// syllables/strokes and turning strokes into SVG path strings. Rendering
// to actual SVG elements happens in components (see
// src/components/sitelenNasin/).

export const CONSONANTS = 'ptksmnljw';
export const VOWELS = 'aeiou';

// Syllables that never occur in toki pona phonology.
export const ILLEGAL_SYLLABLES = ['ji', 'ti', 'wo', 'wu'];

// Layout constants shared by every renderer so glyphs look identical
// in the chart, the quiz, and the playground.
export const CELL_W = 100;
export const BAND_H = 26;
export const BAND_SP = 48;
export const PAD = 12;
export const BLOCK_GAP = 8;

// ---------------------------------------------------------------- geometry

// Consonant gesture: the body of the stroke, drawn between a top line yT
// and bottom line yB starting at x = gx. Returns the path, where the pen
// ends up, and which direction it is travelling (which flavors the vowel
// ending), plus where the pen started (needed for the a- entry loop).
function base(c, gx, yT, yB) {
  switch (c) {
    case 'p': return { d: `M ${gx} ${yT} L ${gx + 44} ${yT} L ${gx + 44} ${yB}`, end: [gx + 44, yB], dir: 'down', start: [gx, yT] };
    case 't': return { d: `M ${gx} ${yT} L ${gx + 40} ${yB}`, end: [gx + 40, yB], dir: 'down', start: [gx, yT] };
    case 'k': return { d: `M ${gx} ${yB} L ${gx + 24} ${yT} L ${gx + 48} ${yB}`, end: [gx + 48, yB], dir: 'down', start: [gx, yB] };
    case 's': return { d: `M ${gx} ${yT} L ${gx + 40} ${yT} L ${gx + 4} ${yB} L ${gx + 44} ${yB}`, end: [gx + 44, yB], dir: 'right', start: [gx, yT] };
    case 'm': return { d: `M ${gx} ${yB} Q ${gx + 24} ${yT - 5} ${gx + 48} ${yB}`, end: [gx + 48, yB], dir: 'down', start: [gx, yB] };
    case 'n': return { d: `M ${gx} ${yT} Q ${gx + 24} ${yB + 6} ${gx + 48} ${yT}`, end: [gx + 48, yT], dir: 'up', start: [gx, yT] };
    case 'l': return { d: `M ${gx} ${yT} L ${gx} ${yB} L ${gx + 44} ${yB}`, end: [gx + 44, yB], dir: 'right', start: [gx, yT] };
    case 'j': return { d: `M ${gx + 16} ${yT} L ${gx + 16} ${yB - 8} Q ${gx + 18} ${yB + 4} ${gx + 30} ${yB}`, end: [gx + 30, yB], dir: 'right', start: [gx + 16, yT] };
    case 'w': return { d: `M ${gx} ${yT} L ${gx + 24} ${yB} L ${gx + 48} ${yT}`, end: [gx + 48, yT], dir: 'up', start: [gx, yT] };
    default: throw new Error(`unknown consonant: ${c}`);
  }
}

// Vowel ending appended where the consonant gesture leaves off. `exit` is
// the direction the pen is travelling when the ending finishes — the coda
// flick uses it to kick away from the glyph instead of back across it.
function term(v, ex, ey, dir) {
  const away = dir === 'up' ? 'up' : 'down';
  if (v === 'a') return { d: '', fp: [ex, ey], exit: away };
  if (v === 'i') {
    if (dir === 'up') return { d: ` L ${ex + 8} ${ey + 13}`, fp: [ex + 8, ey + 13], exit: 'down' };
    return { d: ` L ${ex + 8} ${ey - 13}`, fp: [ex + 8, ey - 13], exit: 'up' };
  }
  if (v === 'u') {
    // Hooks stay compact vertically so they don't reach into the next band.
    if (dir === 'up') return { d: ` Q ${ex + 9} ${ey - 3} ${ex + 2} ${ey - 9}`, fp: [ex + 2, ey - 9], exit: 'up' };
    return { d: ` Q ${ex + 9} ${ey + 3} ${ex + 2} ${ey + 9}`, fp: [ex + 2, ey + 9], exit: 'down' };
  }
  if (v === 'o') {
    // Radius 7 keeps the counter (the hole) open at heavy stroke weights.
    if (dir === 'up') return { d: ' a 7 7 0 1 0 0 -14 a 7 7 0 1 0 0 14', fp: [ex, ey], exit: away };
    if (dir === 'right') return { d: ' a 7 7 0 1 0 14 0 a 7 7 0 1 0 -14 0', fp: [ex, ey], exit: away };
    return { d: ' a 7 7 0 1 0 0 14 a 7 7 0 1 0 0 -14', fp: [ex, ey], exit: away };
  }
  if (v === 'e') {
    // Wave tail: a small S-curve. The curvature reversal is what makes it
    // unmistakable next to u's single-curl hook, and rounded shapes never
    // fill in with ink at heavy stroke weights.
    if (dir === 'up') return { d: ` Q ${ex + 8} ${ey - 8} ${ex + 13} ${ey - 2} Q ${ex + 18} ${ey + 4} ${ex + 23} ${ey - 2}`, fp: [ex + 23, ey - 2], exit: 'up' };
    if (dir === 'right') return { d: ` Q ${ex + 7} ${ey - 8} ${ex + 12} ${ey - 2} Q ${ex + 17} ${ey + 4} ${ex + 22} ${ey - 2}`, fp: [ex + 22, ey - 2], exit: 'up' };
    return { d: ` Q ${ex + 8} ${ey + 8} ${ex + 13} ${ey + 2} Q ${ex + 18} ${ey - 4} ${ex + 23} ${ey + 2}`, fp: [ex + 23, ey + 2], exit: 'down' };
  }
  throw new Error(`unknown vowel: ${v}`);
}

// Full SVG path for one stroke spec inside the band [yT, yB] at x = gx.
export function strokePath(spec, gx, yT, yB) {
  let d;
  let fp;
  let exit;
  if (spec.carrier) {
    const ym = (yT + yB) / 2;
    d = `M ${gx} ${ym} L ${gx + 30} ${ym}`;
    const t = term(spec.vowel, gx + 30, ym, 'right');
    d += t.d;
    fp = t.fp;
    exit = t.exit;
  } else {
    const off = spec.aloop ? 8 : 0;
    const b = base(spec.cons, gx + off, yT, yB);
    d = b.d;
    if (spec.aloop) {
      const [sx, sy] = b.start;
      d = `M ${sx + 2} ${sy + 9} C ${sx - 13} ${sy + 13} ${sx - 13} ${sy - 5} ${sx + 2} ${sy} ` +
        d.replace(`M ${sx} ${sy}`, `L ${sx} ${sy}`);
    }
    const t = term(spec.vowel, b.end[0], b.end[1], b.dir);
    d += t.d;
    fp = t.fp;
    exit = t.exit;
  }
  // Backward flick for final -n, kicked in the direction the pen is
  // already travelling so it never doubles back across the ending.
  if (spec.coda) d += ` L ${fp[0] - 14} ${fp[1] + (exit === 'up' ? -8 : 8)}`;
  return d;
}

// ---------------------------------------------------------------- language

// Parse a toki pona word into syllables [{cons, vowel, coda}].
// cons is null for a bare-vowel syllable; coda is true for a final -n.
// Returns null when the word doesn't fit toki pona phonology.
export function syllabify(word) {
  const w = word.toLowerCase();
  let i = 0;
  const syls = [];
  while (i < w.length) {
    let cons = null;
    if (CONSONANTS.includes(w[i])) {
      if (i + 1 < w.length && VOWELS.includes(w[i + 1])) {
        cons = w[i];
        i += 1;
      } else {
        return null;
      }
    }
    if (i >= w.length || !VOWELS.includes(w[i])) return null;
    const vowel = w[i];
    i += 1;
    let coda = false;
    if (i < w.length && w[i] === 'n' && (i + 1 === w.length || CONSONANTS.includes(w[i + 1]))) {
      coda = true;
      i += 1;
    }
    syls.push({ cons, vowel, coda });
  }
  return syls.length ? syls : null;
}

// Apply fusion rules: a word-initial bare "a" becomes an entry loop on the
// following stroke; other bare vowels ride a carrier dash.
export function strokesFor(syls) {
  const out = [];
  let aloop = false;
  syls.forEach((s, idx) => {
    if (s.cons === null) {
      if (s.vowel === 'a' && !s.coda && idx + 1 < syls.length) {
        aloop = true;
        return;
      }
      out.push({ carrier: true, vowel: s.vowel, coda: s.coda });
      return;
    }
    out.push({ cons: s.cons, vowel: s.vowel, coda: s.coda, aloop });
    aloop = false;
  });
  return out;
}

// Word -> list of blocks, each a list of up to 3 stroke specs (bands).
// Returns null for words that aren't valid toki pona phonology.
export function blocksFor(word) {
  const syls = syllabify(word);
  if (!syls) return null;
  const strokes = strokesFor(syls);
  const blocks = [];
  for (let i = 0; i < strokes.length; i += 3) blocks.push(strokes.slice(i, i + 3));
  return blocks;
}

// ---------------------------------------------------------------- word geometry

// Everything a renderer needs to draw one word: overall size plus a flat
// list of path strings ({d, joiner}) — joiner paths are the faint dashes
// linking consecutive blocks of long words and are usually drawn lighter.
export function wordGeometry(word) {
  const blocks = blocksFor(word);
  if (!blocks) return null;
  const maxBands = Math.max(...blocks.map((b) => b.length));
  const height = PAD * 2 + (maxBands - 1) * BAND_SP + BAND_H;
  const width = blocks.length * CELL_W + (blocks.length - 1) * BLOCK_GAP;
  const paths = [];
  let x = 0;
  blocks.forEach((blk, bi) => {
    if (bi > 0) {
      paths.push({ d: `M ${x - BLOCK_GAP + 1} ${height / 2} L ${x - 1} ${height / 2}`, joiner: true });
    }
    const used = (blk.length - 1) * BAND_SP + BAND_H;
    const y0 = (height - used) / 2;
    blk.forEach((spec, k) => {
      const yT = y0 + k * BAND_SP;
      paths.push({ d: strokePath(spec, x + 22, Math.round(yT), Math.round(yT + BAND_H)), joiner: false });
    });
    x += CELL_W + BLOCK_GAP;
  });
  return { width, height, blocks, paths };
}

// Geometry for a single syllable stroke (used by the chart and drills).
export function syllableGeometry(spec) {
  const width = CELL_W;
  const height = PAD * 2 + BAND_H;
  const d = strokePath(spec, 22, PAD, PAD + BAND_H);
  return { width, height, d };
}

// Latin spelling of one stroke spec, e.g. {cons:'t', vowel:'o'} -> "to",
// {carrier:true, vowel:'e'} -> "e", coda adds "n", aloop prefixes "a".
export function strokeToLatin(spec) {
  const head = spec.carrier ? '' : spec.cons;
  return `${spec.aloop ? 'a' : ''}${head || ''}${spec.vowel}${spec.coda ? 'n' : ''}`;
}

// All 45 legal CV syllables (9 consonants x 5 vowels minus the 4 that
// never occur), as stroke specs — the flashcard deck for syllable drills.
export function legalSyllables() {
  const out = [];
  for (const c of CONSONANTS) {
    for (const v of VOWELS) {
      if (!ILLEGAL_SYLLABLES.includes(c + v)) out.push({ cons: c, vowel: v, coda: false });
    }
  }
  return out;
}

export function isValidWord(word) {
  return syllabify(word) !== null;
}

// ---------------------------------------------------------------- practice

// How confusable two stroke specs are, for building mean multiple-choice
// distractors: sharing a consonant gesture or a vowel ending makes two
// syllables look alike on the page.
function specSimilarity(a, b) {
  let s = 0;
  if (!a.carrier && !b.carrier && a.cons === b.cons) s += 2;
  if ((a.carrier || false) === (b.carrier || false)) s += 1;
  if (a.vowel === b.vowel) s += 2;
  if ((a.coda || false) === (b.coda || false)) s += 1;
  return s;
}

// Pick `count` distractor syllables that look similar to `answer`.
// rng is an injected () => [0,1) so tests can be deterministic.
export function syllableDistractors(answer, rng, count = 3) {
  const pool = legalSyllables().filter((s) => strokeToLatin(s) !== strokeToLatin(answer));
  const scored = pool
    .map((s) => ({ s, score: specSimilarity(s, answer) + rng() * 2 }))
    .sort((x, y) => y.score - x.score);
  return scored.slice(0, count).map((x) => x.s);
}

// How confusable two words are: same number of strokes matters most, then
// shared strokes band-by-band.
export function wordSimilarity(a, b) {
  const sa = strokesFor(syllabify(a) || []);
  const sb = strokesFor(syllabify(b) || []);
  let score = sa.length === sb.length ? 3 : 0;
  const n = Math.min(sa.length, sb.length);
  for (let i = 0; i < n; i += 1) {
    score += specSimilarity(sa[i], sb[i]);
  }
  return score;
}

// Pick `count` distractor words from `pool` that could plausibly be
// misread as `answer`.
export function wordDistractors(answer, pool, rng, count = 3) {
  const scored = pool
    .filter((w) => w !== answer && isValidWord(w))
    .map((w) => ({ w, score: wordSimilarity(w, answer) + rng() * 3 }))
    .sort((x, y) => y.score - x.score);
  return scored.slice(0, count).map((x) => x.w);
}

// Fisher–Yates with injected rng.
export function shuffleWith(arr, rng) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
